import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import React, { useState, useMemo, useRef, useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import { FinleyAiAthenaSqlGeneration } from './FinleyAiAthenaSqlGeneration';
import { FinleyAiDocumentAma } from './FinleyAiDocumentAma';
import { FinleyAiThreads } from './FinleyAiThreads';
import { FinleyAiThreadWindow } from './FinleyAiThreadWindow';
import { FinleyVirtualSheetInputs } from './virtualSheet/FinleyVirtualSheetInputs';
import { UserTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { SplitPane } from 'packages/common-frontend/components/SplitPane';
import { useFinleyAiThreads } from 'packages/finleyAi/hooks/useFinleyAiThreads';
import { FinleyAiExtractValues } from 'packages/finleyAi/ui/FinleyAiExtractValues';
import { FinleyAiTransform } from 'packages/finleyAi/ui/FinleyAiTransform';
import {
  finleyAiSelectOptions,
  getPromptPlaceholder,
  initFinleyAiViewContext,
} from 'packages/finleyAi/ui/FinleyAiUtils';
import { RiChatNewFill } from 'react-icons/ri';

export const SIZES = {
  borderRadius: '8px',
};

type FinleyAiModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  targetOrganizationId: string;
  type: FinleyAiTypes.FinleyAiType;
  userId: string;
  organizations?: { id: string; slug: string }[];
  userType: UserTypeGQLType;
};

export const FinleyAiModal = ({
  isOpen,
  targetOrganizationId: initialTargetOrganizationId,
  onCancel,
  type,
  userId,
  organizations,
  userType,
}: FinleyAiModalProps) => {
  // set initial focus on prompt input text area
  // because default focus is on modal header, which displays IconButton tooltip in awkward location
  const promptInputTextArea = useRef<HTMLTextAreaElement>(null);

  const [tempPrompt, setTempPrompt] = useState<string>('');

  const [prompt, setPrompt] = useState<string>('');

  const initialViewContext = initFinleyAiViewContext({
    type,
    targetOrganizationId: initialTargetOrganizationId,
  });

  const {
    onReset,
    selectedThread,
    isThreadLoading,
    viewContext,
    setViewContext,
    isThreadFailed,
    onSendPrompt,
    handleAthenaSqlExplain,
    handleDocumentAmaUpload,
    isProducingMessage,
    handleFinleyAiThreadSelection,
  } = useFinleyAiThreads({
    prompt,
    setPrompt,
    tempPrompt,
    setTempPrompt,
    initialViewContext,
  });

  const {
    type: finleyAiType,
    finleyAiThreadId,
    targetOrganizationId,
  } = viewContext;

  const finleyAiOrgSelections = useMemo(() => {
    if (organizations) {
      finleyAssert(
        userType === 'SUPER_USER',
        'Must be superuser to use org selector.',
      );
      return organizations.map(({ id, slug }) => ({
        label: slug,
        value: id,
      }));
    }
    return null;
  }, [organizations, userType]);

  const onCopyHandler: (text: string) => Promise<void> = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
    },
    [],
  );

  const isAskButtonDisabled = useMemo(() => {
    if (isThreadFailed) {
      return true;
    }

    if (viewContext.type === 'extractValues' && viewContext.s3Path === '') {
      return true;
    }

    if (viewContext.type === 'documentAma' && viewContext.tool !== '') {
      try {
        JSON.parse(viewContext.tool);
        return false;
      } catch {
        return true;
      }
    }

    if (viewContext.type === 'transform' && viewContext.tool !== '') {
      try {
        JSON.parse(viewContext.tool);
        return false;
      } catch {
        return true;
      }
    }

    return false;
  }, [isThreadFailed, viewContext]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
      initialFocusRef={promptInputTextArea}
    >
      <ModalOverlay />
      <ModalContent
        p="5"
        width="95vw"
        height="90vh"
        maxWidth="95vw"
        minHeight="90vh"
        overflowY="auto"
      >
        <ModalHeader>
          <Flex alignItems="center" width="100%">
            <Text mr="3px">Finley AI</Text>
            <IconButton
              icon={<RiChatNewFill />}
              ariaLabel="New thread"
              label="New thread"
              onClick={() =>
                // do not change type or orgId on init new thread
                onReset({
                  finleyAiType: viewContext.type,
                  targetOrgId: viewContext.targetOrganizationId,
                })
              }
            />
            <Flex alignItems="center" ml="auto" fontSize="medium" width="500px">
              <Text mr="3px">Mode:</Text>
              <Select
                value={finleyAiType}
                onChange={(val) => {
                  if (val) {
                    setViewContext(
                      initFinleyAiViewContext({
                        type: val,
                        targetOrganizationId,
                      }),
                    );
                    setPrompt('');
                  }
                }}
                options={finleyAiSelectOptions}
                width="sm"
                isDisabled={finleyAiThreadId !== null || isThreadLoading}
              />
              {finleyAiOrgSelections && (
                <Select
                  value={targetOrganizationId}
                  onChange={(val) => {
                    if (val) {
                      setViewContext(
                        initFinleyAiViewContext({
                          type: finleyAiType,
                          targetOrganizationId: val,
                        }),
                      );
                      setPrompt('');
                    }
                  }}
                  width="auto"
                  options={finleyAiOrgSelections}
                  isDisabled={finleyAiThreadId !== null || isThreadLoading}
                />
              )}
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%" height="100%" display="flex" flexDir="column">
          <Flex height="100%" width="100%">
            {/* Outer horizontal split: Left threads vs. Middle+Right */}
            <SplitPane
              direction="horizontal"
              // Left used to be ~30% of total width
              defaultSize={30}
              min={15}
              max={70}
              height="100%"
              start={
                <FinleyAiThreads
                  userId={userId}
                  handleFinleyAiThreadSelection={handleFinleyAiThreadSelection}
                  finleyAiThreadId={finleyAiThreadId}
                />
              }
              end={
                // Inner horizontal split: Middle conversation vs. Right config panel
                <SplitPane
                  direction="horizontal"
                  // Middle used to be ~40% of total; within remaining 70% this is ~57%
                  defaultSize={57}
                  min={30}
                  max={85}
                  height="100%"
                  start={
                    // Vertical split in the middle: Thread window (top) vs. prompt textarea (bottom)
                    <SplitPane
                      direction="vertical"
                      defaultSize={75}
                      min={40}
                      max={90}
                      height="100%"
                      start={
                        <Flex
                          height="100%"
                          border="1px"
                          p="16px"
                          borderRadius={SIZES.borderRadius}
                          borderColor={colors.gray[600]}
                          overflowY="auto"
                        >
                          <FinleyAiThreadWindow
                            thread={selectedThread}
                            isThreadDisabled={isThreadFailed}
                            isProducingMessage={isProducingMessage}
                            isGeneratingPromptResponse={isThreadLoading}
                            handleAthenaSqlExplain={handleAthenaSqlExplain}
                            onClickSqlAction={{
                              callback: onCopyHandler,
                              label: 'Copy SQL',
                            }}
                            tempPrompt={tempPrompt}
                          />
                        </Flex>
                      }
                      end={
                        <Textarea
                          height="100%"
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          placeholder={getPromptPlaceholder({
                            type: finleyAiType,
                            loading: isThreadLoading,
                            isThreadFailed,
                          })}
                          ref={promptInputTextArea}
                          isDisabled={
                            isThreadLoading ||
                            isThreadFailed ||
                            finleyAiType === 'extractValues'
                          }
                        />
                      }
                    />
                  }
                  end={
                    <Flex
                      height="100%"
                      border="1px"
                      borderColor={colors.gray[600]}
                      p="3px"
                      borderRadius={SIZES.borderRadius}
                      overflowX="auto"
                    >
                      {viewContext.type === 'athenaSqlGeneration' && (
                        <FinleyAiAthenaSqlGeneration
                          targetOrganizationId={targetOrganizationId}
                          viewContext={viewContext}
                          setViewContext={setViewContext}
                          selectedTableNames={viewContext.tableNames}
                          isLocked={finleyAiThreadId !== null}
                        />
                      )}
                      {viewContext.type === 'documentAma' && (
                        <FinleyAiDocumentAma
                          targetOrganizationId={targetOrganizationId}
                          viewContext={viewContext}
                          setViewContext={setViewContext}
                          selectedKnowledgeBaseDocumentIds={
                            viewContext.knowledgeBaseDocumentIds
                          }
                          isLocked={finleyAiThreadId !== null}
                          handleDocumentAmaUpload={handleDocumentAmaUpload}
                        />
                      )}
                      {viewContext.type === 'extractValues' && (
                        <FinleyAiExtractValues
                          targetOrganizationId={targetOrganizationId}
                          viewContext={viewContext}
                          setViewContext={setViewContext}
                          isLocked={isThreadFailed || isThreadLoading}
                        />
                      )}
                      {viewContext.type === 'virtualSheetConfig' && (
                        <FinleyVirtualSheetInputs
                          targetOrganizationId={targetOrganizationId}
                          viewContext={viewContext}
                          setViewContext={setViewContext}
                          isLocked={isThreadFailed || isThreadLoading}
                        />
                      )}
                      {viewContext.type === 'transform' && (
                        <FinleyAiTransform
                          targetOrganizationId={targetOrganizationId}
                          viewContext={viewContext}
                          setViewContext={setViewContext}
                          isLocked={isThreadFailed || isThreadLoading}
                        />
                      )}
                    </Flex>
                  }
                />
              }
            />
          </Flex>
          <Flex justifyContent="center" pt="10px">
            <Button
              type="button"
              mr="5"
              onClick={() => {
                onSendPrompt();
              }}
              isLoading={isThreadLoading}
              isDisabled={isAskButtonDisabled}
            >
              Ask
            </Button>

            <Button onClick={() => onCancel()}>Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
